import { Card, Empty, Input, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Box, Boxes, Database, Search, Server, Workflow } from "lucide-react";
import { useMemo, useState } from "react";
import type { SchedulerResource } from "../../../backend/cost-optimizer";

const { Text } = Typography;

interface Props {
    resources: SchedulerResource[];
    loading: boolean;
}

const STATE_COLOR: Partial<Record<SchedulerResource["state"], string>> = {
    running: "success",
    stopped: "default",
    starting: "processing",
    stopping: "warning",
    transitioning: "warning",
    failed: "error"
};

function ResourceIcon({ service }: { service: SchedulerResource["service"] }) {
    if (service === "RDS") return <Database size={16} />;
    if (service === "EKS") return <Boxes size={16} />;
    if (service === "ECS" || service === "Fargate") return <Workflow size={16} />;
    if (service === "Auto Scaling") return <Box size={16} />;
    return <Server size={16} />;
}

function resourceDescription(resource: SchedulerResource) {
    if (resource.type === "eks-node-group") {
        return `${resource.details.clusterName} · ${resource.details.desiredSize} desired · min ${resource.details.minSize} / max ${resource.details.maxSize}`;
    }
    return resource.name ? resource.id : resource.type;
}

export default function ResourceInventory({ resources, loading }: Props) {
    const [query, setQuery] = useState("");
    const [service, setService] = useState("all");
    const [management, setManagement] = useState("managed");

    const filtered = useMemo(() => resources.filter(resource => {
        const text = `${resource.name} ${resource.id} ${resource.service}`.toLowerCase();
        return (!query || text.includes(query.toLowerCase()))
            && (service === "all" || resource.service === service)
            && (management === "all" || (management === "managed" ? resource.managed : !resource.managed));
    }), [resources, query, service, management]);

    const columns: ColumnsType<SchedulerResource> = [
        {
            title: "Resource",
            key: "resource",
            ellipsis: true,
            render: (_, resource) => (
                <div className="optimizer-resource-name">
                    <span className="optimizer-resource-icon"><ResourceIcon service={resource.service} /></span>
                    <div>
                        <strong>{resource.name || resource.id}</strong>
                        <Text type="secondary" ellipsis>{resourceDescription(resource)}</Text>
                    </div>
                </div>
            )
        },
        {
            title: "Service",
            dataIndex: "service",
            key: "service",
            width: 120
        },
        {
            title: "State",
            key: "state",
            width: 130,
            render: (_, resource) => <Tag color={STATE_COLOR[resource.state]}>{resource.state}</Tag>
        },
        {
            title: "Scheduler",
            key: "management",
            width: 165,
            render: (_, resource) => {
                if (resource.lastError) return <Tag color="error">Needs attention</Tag>;
                if (resource.managed && resource.eligible) return <Tag color="blue">Managed</Tag>;
                return <Text type="secondary" title={resource.eligibilityReason || undefined}>{resource.eligibilityReason || "Excluded"}</Text>;
            }
        }
    ];

    return (
        <Card
            className="optimizer-content-card optimizer-inventory"
            title={<div><span>Resources</span><Text type="secondary">Live state from AWS</Text></div>}
            extra={<Text type="secondary">{resources.length} discovered</Text>}
        >
            <div className="optimizer-resource-toolbar">
                <Input
                    allowClear
                    prefix={<Search size={15} />}
                    placeholder="Search resources"
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                />
                <Space>
                    <Select
                        value={service}
                        onChange={setService}
                        options={[
                            { value: "all", label: "All services" },
                            ...["EC2", "EKS", "Fargate", "ECS", "RDS", "Auto Scaling"].map(value => ({ value, label: value }))
                        ]}
                    />
                    <Select
                        value={management}
                        onChange={setManagement}
                        options={[
                            { value: "managed", label: "Managed" },
                            { value: "all", label: "All resources" },
                            { value: "excluded", label: "Excluded" }
                        ]}
                    />
                </Space>
            </div>
            <Table
                rowKey={resource => `${resource.type}:${resource.id}`}
                columns={columns}
                dataSource={filtered}
                loading={loading}
                size="middle"
                pagination={filtered.length > 8 ? { pageSize: 8, showSizeChanger: false } : false}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={query ? "No resources match your search" : "No tagged resources found"} /> }}
            />
        </Card>
    );
}
